import { DELIMITERS } from "./../messages/delimiters";

export class MathStringsUtils {
  public isMathWithDelimiters(latex?: string): boolean {
    if (!latex || latex.length === 0 || latex === undefined) {
      return false;
    }

    return (
      (latex.startsWith(DELIMITERS.LATEX_START) && latex.endsWith(DELIMITERS.LATEX_END)) ||
      (latex.startsWith('$$') && latex.endsWith('$$'))
    );
  }

  public hasMathDelimiters(latex?: string): boolean {
    if (!latex || latex.length === 0 || latex === undefined) {
      return false;
    }

    return latex.includes(DELIMITERS.LATEX_START) && latex.includes(DELIMITERS.LATEX_END);
  }

  public hasMathMultiLineDelimiters(latex: string): boolean {
    if (!latex) {
      return false;
    }

    return latex.includes(DELIMITERS.LATEX_STEP_SEPARATOR);
  }

  public convertLatexArrayToDelimitedLatexString(latexArray: string[]): string {
    if (!latexArray || latexArray.length === 0) {
      return "";
    }

    const latex = latexArray.length === 1 ? latexArray[0] : latexArray.join(DELIMITERS.LATEX_STEP_SEPARATOR);
    const delimitedLatex = `${DELIMITERS.LATEX_START}${latex}${DELIMITERS.LATEX_END}`;
    return delimitedLatex;
  }

  public convertDelimitedLatexStringToLatexArray(delimitedLatex: string): string[] {
    if (!delimitedLatex || !this.isMathWithDelimiters(delimitedLatex)) {
      return [];
    }

    let innerLatex = delimitedLatex;
    if (delimitedLatex.startsWith(DELIMITERS.LATEX_START) && delimitedLatex.endsWith(DELIMITERS.LATEX_END)) {
      innerLatex = delimitedLatex.slice(DELIMITERS.LATEX_START.length, -DELIMITERS.LATEX_END.length);
    }

    return innerLatex.split(DELIMITERS.LATEX_STEP_SEPARATOR).map((latex) => latex.trim());
  }

  public addLatexAlignmentForMathJax(latexArray: string[]): string {
    if (!latexArray || latexArray.length === 0) return "";

    const joined = latexArray.join("\\\\\\\\&");
    const wrapped = `\\begin{align*}& ${joined} \\end{align*}`;

    // Se è già in formato align*, evita di modificarlo
    const isAlreadyAligned =
      (latexArray.length === 1 && latexArray[0].trim().startsWith("\\begin{align*}") && latexArray[0].trim().endsWith("\\end{align*}")) ||
      (latexArray.length > 1 && latexArray.join("").includes("\\begin{align*}") && latexArray.join("").includes("\\end{align*}"));

    return isAlreadyAligned ? latexArray.join("") : wrapped;
  }

  public convertLatexSupportedSymbols(latex: string): string {
    if (!latex || latex.length === 0) return "";

    let l = "";
    l = latex;
    l = l.replace(/\\sfrac/g, "\\frac");
    l = l.replace(/\\sen/g, "\\mbox{sen}");
    l = l.replace(/\\bm/g, "\\boldsymbol");
    l = l.replace(/\\arccot/g, "\\mbox{arccot}"); // Non esiste in Latex, ma in MateMitica sì!
    l = l.replace(/\\bm/g, "\\boldsymbol");
    return l;
  }

  private latexPlaceholderImageSymbol = "□";

  public convertPlaceholderSymbol(latex: string): string {
    if (!latex || latex.length === 0) return "";
    return latex.replace(/\\placeholder\b/g, this.latexPlaceholderImageSymbol);
  }

  public convertMathMLSupportedSymbols(mathML: string): string {
    let l = "";
    try {
      //l = MathMLToLaTeX.convert('<math>' + mathML + '</math>');
      l = mathML;
      l = l.replace(/\\degree/g, "°");
      l = l.replace(/\\overarc/g, "\\widehat");
    } catch (err) {
      console.log("getMathJaxLatex ERROR: " + JSON.stringify(err));
    }
    return l;
  }

  public fixLatexFromOcr(ltx: string): string {
    let s = ltx;
    s = s.replace(/\(/g, "\\left (");
    s = s.replace(/\)/g, "\\right )");

    s = s.replace(/(?<!\\sqrt)\[/g, "\\left \\lbrack");
    s = s.replace(/(?<!\\sqrt)\]/g, "\\right \\rbrack");
    s = s.replace(/\\\{/g, "\\left \\lbrace");
    s = s.replace(/\\\}/g, "\\right \\rbrace");
    // Fix double left and right
    s = s.replace(/\\left\\left/g, "\\left");
    s = s.replace(/\\right\\right/g, "\\right");
    return s;
  }

  public convertHtmlToText(html: string): string {
    const tempDiv = document.createElement("div");

    tempDiv.innerHTML = html;

    let text = tempDiv.textContent || "";

    text = text.replace(/\u00A0/g, " "); // &nbsp;
    text = text.replace(/\s+/g, " ").trim(); // elimina spazi multipli

    tempDiv.remove();

    return text;
  }

  public extractDelimitedLatexExpressions(text: string): string[] {
    const regex = new RegExp(`${DELIMITERS.LATEX_START}(.*?)${DELIMITERS.LATEX_END}`, "gs");
    const matches = text.matchAll(regex);
    return Array.from(matches)
      .map((m) => m[0]) // tieni il match completo, inclusi i delimitatori
      .filter((fullMatch) => {
        const innerContent = fullMatch.slice(DELIMITERS.LATEX_START.length, -DELIMITERS.LATEX_END.length).trim();
        return innerContent.length > 0;
      });
  }

  public replaceLatexDollarsWithDelimiters(html: string): string {
    if (!html) {
      return html;
    }

    const latexBlockRegex = /\$\$([\s\S]*?)\$\$/g;

    return html.replace(latexBlockRegex, (_match, inner) => {
      return `@@@STARTLATEX@@@${inner}@@@ENDLATEX@@@`;
    });
  }

  /**
   * Sostituisce i simboli \cancel{...}^{...} con il contenuto dell'apice,
   * sempre racchiuso tra parentesi tonde per preservare il significato matematico.
   * Gestisce anche i simboli \cancel annidati processandoli ricorsivamente.
   *
   * @param latexString - Stringa LaTeX contenente simboli \cancel
   * @returns Stringa LaTeX con le sostituzioni applicate
   */
  public processCancelSymbols(latexString: string): string {
    if (latexString === undefined || latexString === "") return "";

    let result = latexString;
    let hasChanges = true;

    // Continua a processare finché non ci sono più sostituzioni da fare
    while (hasChanges) {
      const beforeProcessing = result;
      result = this.processCancelSymbolsOnce(result);
      hasChanges = beforeProcessing !== result;
    }

    return result;
  }

  /**
   * Esegue una singola passata di sostituzione dei simboli \cancel
   */
  public processCancelSymbolsOnce(input: string): string {
    // Regex per trovare pattern \cancel{...}^{...} o \cancel{...}^contenuto
    // Gestisce sia esponenti con parentesi graffe che senza
    const cancelPattern = /\\cancel{((?:[^{}]|{[^{}]*})*)}\^({(?:[^{}]|{[^{}]*})*}|[^{}\\]+)/g;

    return input.replace(cancelPattern, (match, cancelContent, exponentContent) => {
      // Se l'esponente ha le parentesi graffe, le rimuoviamo
      let cleanExponent = exponentContent;
      if (cleanExponent.startsWith("{") && cleanExponent.endsWith("}")) {
        cleanExponent = cleanExponent.slice(1, -1);
      }

      // Sempre parentesi tonde per sicurezza
      return `(${cleanExponent})`;
    });
  }

  /**
   * Pulisce i tag HTML all'interno dei delimitatori LaTeX, mantenendo la formattazione
   * dei contenuti esterni ai delimitatori. Il contenuto dentro i delimitatori viene
   * convertito in plain text.
   *
   * @param htmlString - Stringa HTML contenente delimitatori LaTeX
   * @returns Stringa HTML con i tag rimossi solo all'interno dei delimitatori
   */
  public cleanHtmlInsideDelimiters(htmlString: string): string {
    if (!htmlString || htmlString.length === 0) {
      return "";
    }

    // Regex per trovare tutto ciò che è dentro i delimitatori (inclusi i delimitatori stessi)
    const delimiterRegex = new RegExp(
      `(${this.escapeRegex(DELIMITERS.LATEX_START)})(.*?)(${this.escapeRegex(DELIMITERS.LATEX_END)})`,
      "gs",
    );

    return htmlString.replace(delimiterRegex, (match, startDelimiter, content, endDelimiter) => {
      // Converte il contenuto HTML in plain text mantenendo solo il testo
      const cleanContent = this.convertHtmlToText(content);

      // Ricostruisce il match con il contenuto pulito
      return startDelimiter + cleanContent + endDelimiter;
    });
  }

  /**
   * Escape dei caratteri speciali per regex
   * @param string - Stringa da escapare
   * @returns Stringa con caratteri speciali escapati
   */
  private escapeRegex(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  /**
   * Escape dei simboli significativi del latex
   * @param text Testo da convertire
   * @returns Testo compatibile con latex
   */
  escapeLaTeX(text: string): string {
    // Mappa completa dei caratteri speciali LaTeX e loro escape (per text mode)
    const escapes: { [key: string]: string } = {
      "{": "\\{",
      "}": "\\}",
      "\\": "\\textbackslash{}", // Backslash richiede pacchetto textcomp o simile
      "#": "\\#",
      $: "\\$",
      "%": "\\%",
      "&": "\\&",
      _: "\\_",
      "^": "\\^{}",
      "~": "\\textasciitilde{}", // Spazio non-breaking in LaTeX
    };

    // Regex per matchare tutti i caratteri speciali (globale, case-sensitive)
    return text.replace(/[\\#\\$%&_\\^{}{~}]/g, (match) => escapes[match]);
  }
}
